/**
 * LinkedIn-enrichment side effect: the "[Rep] Find Email from LinkedIn" Clay
 * routine, fired either automatically the moment a live-camera QR scan
 * completes (`ScanReview.tsx`'s mount effect) or manually via the "Enrich
 * with Clay" button (also `ScanReview.tsx`).
 *
 * Confirmed live via the Clay MCP `surfaces_read` tool (do not re-derive):
 *   - Routine id `t_0tb2t33MwuDPSQiQhpG`.
 *   - Input schema `{ "LinkedIn Profile": string (uri) }` — the exact key is
 *     `"LinkedIn Profile"`, NOT `"Linkedin"` (that's the DIFFERENT
 *     `lead_scanning` routine's own input key — see `submit-clay.ts`).
 *   - Output schema `{ "Full Name": string, "Work Email": string,
 *     "Company Name": string }` — the email output key is `"Work Email"`,
 *     NOT `"Email"`.
 *
 * This is a side-effect-free lookup with no SFDC write and no notification,
 * so it calls `_shared/clay-routine.ts`'s `executeClayRoutine` directly
 * (`api.ts`'s `POST /enrich-linkedin`) rather than going through the heavy
 * `ActionDefinition`/action-execution/poll pipeline `submit-clay.ts` uses for
 * the stateful, crash-recoverable, audited Salesforce write.
 *
 * Deliberately a zero-backend-import leaf (same reasoning as
 * `linkedin-qr.ts`'s file header): `shouldAutoEnrich` below is imported by
 * `ScanReview.tsx` (frontend), so this module must stay bundle-safe.
 * `mapEnrichClayResult` — only ever called backend-side, from `api.ts` — is
 * typed against a LOCAL structural shape of `_shared/clay-routine.ts`'s
 * `HandlerResult` rather than importing that type, and the `HANDLER_PENDING`
 * code is duplicated as a literal (mirroring `CLAY_ROUTINE_PENDING_CODE`)
 * rather than imported, so nothing in this file's module graph ever pulls
 * `_shared/clay-routine.ts`'s backend-only re-export (prisma, node fetch,
 * `src/services/execution-handlers/clay-routine`) into a frontend bundle.
 */

import { z } from 'zod';

/** Live-verified, see file header — never re-derive this from a guess. */
export const LEAD_SCANNER_ENRICH_ROUTINE_ID = 'function:t_0tb2t33MwuDPSQiQhpG';

/**
 * Builds the routine's `inputs` object. Pure — the caller (`api.ts`) is
 * responsible for normalizing/validating the value BEFORE it reaches here
 * (via `linkedin-qr.ts`'s `normalizeLinkedInInput`/`isLinkedInProfileUrl`);
 * this function does nothing but apply the exact input label.
 */
export function buildEnrichLinkedInInput(linkedin: string): Record<string, string> {
  return { 'LinkedIn Profile': linkedin };
}

/** The routine's own output fields, live-verified (see file header). */
const EnrichClayRoutineResultSchema = z
  .object({
    'Full Name': z.string().nullish(),
    'Work Email': z.string().nullish(),
    'Company Name': z.string().nullish(),
  })
  .passthrough();

/** `null`/`''`/whitespace all mean "the routine didn't populate this" —
 *  same convention as `submit-clay.ts`'s `nullableString`. */
function nullableString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : value;
}

/** Mirrors `_shared/clay-routine.ts`'s `CLAY_ROUTINE_PENDING_CODE` — see the
 *  file header for why this is a literal, not an import. */
const HANDLER_PENDING_CODE = 'HANDLER_PENDING';

/**
 * Minimal structural shape of `_shared/clay-routine.ts`'s `HandlerResult`,
 * duplicated locally (not imported — see file header) so this module stays a
 * frontend-safe leaf. `executeClayRoutine`'s real return value satisfies this
 * shape structurally; `api.ts`'s call site passes it straight through.
 */
export interface ClayRoutineHandlerResultLike {
  ok: boolean;
  code: string;
  message: string;
  meta?: { result?: unknown } | Record<string, unknown> | null;
}

export interface EnrichLinkedInFields {
  name: string | null;
  company: string | null;
  email: string | null;
}

export type EnrichLinkedInOutcome =
  | { ok: true; data: EnrichLinkedInFields }
  | { ok: false; pending: true; message: string }
  | { ok: false; pending: false; message: string };

/**
 * Maps one `executeClayRoutine` result to the wire shape `POST
 * /enrich-linkedin` answers with. Pure — the route does the I/O.
 *
 * - `ok: false` with the pending code → `{pending: true}` (504 upstream —
 *   the inline poll budget expired before the routine finished).
 * - `ok: false` with any other code → `{pending: false}` (502 upstream).
 * - `ok: true` but the result fails schema validation (missing/malformed
 *   `meta.result`) → also `{pending: false}` — a delivered-but-unreadable
 *   result is a failure, never a throw and never a silent success (same
 *   stance as `submit-clay.ts`'s `submitResultFromExecutionRow`).
 * - `ok: true` with a valid result → the three fields, each nulled out when
 *   blank/whitespace/absent rather than passed through as `''`.
 */
export function mapEnrichClayResult(result: ClayRoutineHandlerResultLike): EnrichLinkedInOutcome {
  if (!result.ok) {
    if (result.code === HANDLER_PENDING_CODE) {
      return { ok: false, pending: true, message: result.message };
    }
    return { ok: false, pending: false, message: result.message };
  }

  const meta = result.meta as { result?: unknown } | null | undefined;
  const parsed = EnrichClayRoutineResultSchema.safeParse(meta?.result);
  if (!parsed.success) {
    return {
      ok: false,
      pending: false,
      message: 'The Clay routine finished but returned an unreadable result.',
    };
  }

  return {
    ok: true,
    data: {
      name: nullableString(parsed.data['Full Name']),
      company: nullableString(parsed.data['Company Name']),
      email: nullableString(parsed.data['Work Email']),
    },
  };
}

/** The subset of a scan row `shouldAutoEnrich` needs — real Prisma/wire field
 *  names from `scan.ts`'s `Scan` shape, picked narrowly so this predicate
 *  stays easy to unit test with a plain object literal. */
export interface ScanForAutoEnrich {
  status: string;
  sourceType: string;
  photoFilename: string | null;
  linkedin: string;
  name: string;
  company: string;
  email: string;
}

/**
 * Eligibility predicate for the auto-fire path (`ScanReview.tsx`'s mount
 * effect): true only for a freshly-created live-camera-QR draft that has a
 * LinkedIn URL and nothing else yet — the `sourceType === 'photo' &&
 * photoFilename === null` combination is what uniquely identifies that path
 * (`LinkedInQrScanner.tsx`'s `createScan` call passes `photoFilename: null`;
 * the still-photo intake path always has a real filename). Any of
 * name/company/email already being non-blank means either a still-photo
 * extraction already ran, or the rep already started filling the form by
 * hand — either way, auto-firing would risk clobbering real data with a
 * stale/duplicate lookup.
 */
export function shouldAutoEnrich(scan: ScanForAutoEnrich): boolean {
  return (
    scan.status === 'draft' &&
    scan.sourceType === 'photo' &&
    scan.photoFilename === null &&
    scan.linkedin !== '' &&
    !scan.name &&
    !scan.company &&
    !scan.email
  );
}

/** `ScanForAutoEnrich` plus the `id` `scansEligibleForSessionAutoEnrich`
 *  needs to check membership in a session batch. */
export interface ScanForSessionAutoEnrich extends ScanForAutoEnrich {
  id: string;
}

/**
 * `EnrichmentManager.tsx`'s eligibility filter — the always-mounted
 * replacement for the old per-`ScanReview`-mount auto-fire query (that query
 * died with the component when the rep navigated away mid-run; this manager
 * is mounted once at the app root, `index.tsx`, so it survives navigation).
 *
 * Two gates, both required: `shouldAutoEnrich` (unchanged — a fresh
 * live-camera-QR draft with nothing else filled yet) AND "created THIS
 * session" (`scan.id` is one of `intakeBatchIds`, `store.ts`'s
 * `completeIntake`/`setIntakeBatch`). The second gate is why this function
 * exists at all rather than the manager just calling `shouldAutoEnrich` over
 * the whole scans list on every render: doing that would auto-fire the Clay
 * routine against every stale eligible draft already sitting in the list
 * the moment the app mounts — a surprise real Clay-routine-execution cost
 * per old row the rep never asked for. Scoping to the session batch means
 * only scans the rep just created are ever auto-enriched.
 */
export function scansEligibleForSessionAutoEnrich<T extends ScanForSessionAutoEnrich>(
  intakeBatchIds: string[],
  scans: T[],
): T[] {
  const batchIds = new Set(intakeBatchIds);
  return scans.filter((scan) => batchIds.has(scan.id) && shouldAutoEnrich(scan));
}

/** `POST /enrich-linkedin` response shape — parsed by the frontend
 *  (`useScanApi.ts`'s `enrichLinkedIn`) per the schema-first data-boundary
 *  convention; this app fully controls the request body, so only the
 *  response crosses a trust boundary worth `safeParse`ing. */
export const EnrichLinkedInResponseSchema = z.object({
  name: z.string().nullable(),
  company: z.string().nullable(),
  email: z.string().nullable(),
});
export type EnrichLinkedInResponse = z.infer<typeof EnrichLinkedInResponseSchema>;
